import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc } from 'firebase/firestore';
import { getAuth, updateEmail as firebaseUpdateEmail } from 'firebase/auth';

import { useAuth } from '~/services/auth';
import { logEvent, db } from '~/config/firebase.config';
import { STRIPE_PORTAL_LOGIN_URL } from '~/config/global.config';
import { isAirportClient } from '~/config/airports.config';
import { StripeServerForm } from '~/components/billing/StripeServerForm';
import { useSyncClient } from '~/hooks/models/useClient';
import { analyticsAllowed } from '~/services/consent/consentScope';
import { useSyncUser } from '~/hooks/models/useUser';
import { getSubscriptionStatus, SubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';
import { openCancelSession } from '~/services/subscription/subscriptionActions';
import { readStoredClientID } from '~/utils/storedClientID';
import { unixDate } from '~/utils/pauseFormat';
import { AnnualLinkState, annualLinkState, annualOfferFor } from '~/utils/annualOffer';
import { planUpgradesFor } from '~/utils/planUpgrades';
import { gateProOffer, proBlockFor, proEndedOfferFor, proOfferFor } from '~/utils/proOffer';
import { canKeep, isCancelScheduled, keepEndAt, resolveLinkClient } from '~/utils/keepOffer';
import { canRefer } from '~/utils/referral';
import { formatAmount, getTrialPrice } from '~/utils/trialPricing';
import {
    ACCOUNT_MENU_TAB,
    AccountFocus,
    AccountSection,
    accountPanelId,
    accountTabId,
    resolveAccountSection,
    subscriptionSummary,
} from '~/utils/accountSections';

import styles from './Account.redesign.module.css';
import { TrialStatusCard } from './TrialStatusCard';
import { PauseStatusCard } from './PauseStatusCard';
import { KeepSubscriptionCard } from './KeepSubscriptionCard';
import { KeepThanksCard } from './KeepThanksCard';
import { AnnualLinkNotice, AnnualOfferCard } from './AnnualOfferCard';
import { PlanUpgradesCard } from './PlanUpgradesCard';
import { ReferralCard } from './ReferralCard';
import { PlayerLanguageCard } from './PlayerLanguageCard';
import { BrandingCard } from './BrandingCard';
import { ConsentCard } from './ConsentCard';
import { AccountMenu, AccountMenuItem } from './AccountMenu';
import { Svg } from './Svg';
import { LoadingPage } from '../LoadingPage';

/** Espaces de démo (règles Firestore `isDemo`) : lecture seule pour ce réglage. */
const DEMO_CLIENT_IDS = ['demo', 'airports-demo'];

/** Lien de l'email d'offre annuelle sans encart à montrer : message affiché (clé de traduction). */
const ANNUAL_LINK_NOTICES: Partial<Record<AnnualLinkState, string>> = {
    already_yearly: 'account.annual.linkAlreadyYearly',
    admin_only: 'account.annual.linkAdminOnly',
    unavailable: 'account.annual.linkUnavailable',
    error: 'account.annual.linkError',
};

/* ---------- inline icons (stroke = currentColor) ---------- */
function IconUser() {
    return (
        <Svg>
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </Svg>
    );
}
function IconBuilding() {
    return (
        <Svg>
            <path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M19 21V11a1 1 0 0 0-1-1h-3" />
            <path d="M9 7h2M9 11h2M9 15h2" />
        </Svg>
    );
}
function IconShield() {
    return (
        <Svg>
            <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />
            <path d="M9 12l2 2 4-4" />
        </Svg>
    );
}
function IconCard() {
    return (
        <Svg>
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <path d="M2 10h20" />
        </Svg>
    );
}
function IconSave() {
    return (
        <Svg>
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <path d="M17 21v-8H7v8M7 3v5h8" />
        </Svg>
    );
}
function IconCheck() {
    return (
        <Svg>
            <path d="M20 6 9 17l-5-5" />
        </Svg>
    );
}
function IconMail() {
    return (
        <Svg>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
        </Svg>
    );
}
function IconClose() {
    return (
        <Svg>
            <path d="M18 6 6 18M6 6l12 12" />
        </Svg>
    );
}
function IconPower() {
    return (
        <Svg>
            <path d="M18.36 6.64A9 9 0 1 1 5.64 6.64M12 2v10" />
        </Svg>
    );
}

function IconPalette() {
    return (
        <Svg>
            <circle cx="13.5" cy="6.5" r="1.5" />
            <circle cx="17.5" cy="10.5" r="1.5" />
            <circle cx="8.5" cy="7.5" r="1.5" />
            <circle cx="6.5" cy="12.5" r="1.5" />
            <path d="M12 2a10 10 0 1 0 0 20c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.3 0-1.1.9-2 2-2h2.4A5.6 5.6 0 0 0 22 9.8C22 5.5 17.5 2 12 2z" />
        </Svg>
    );
}
function IconGift() {
    return (
        <Svg>
            <rect x="3" y="8" width="18" height="4" rx="1" />
            <path d="M12 8v13M19 12v9H5v-9M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5" />
        </Svg>
    );
}
function IconBack() {
    return (
        <Svg>
            <path d="m15 18-6-6 6-6" />
        </Svg>
    );
}

type Msg = { type: 'error' | 'success'; text: string } | null;

function MsgText({ msg }: { msg: Msg }) {
    if (!msg) return null;
    return (
        <span className={`${styles.msg} ${msg.type === 'error' ? styles.msgError : styles.msgSuccess}`}>
            {msg.text}
        </span>
    );
}

function Spinner() {
    return <span className={styles.spinnerDot} aria-label="Loading" />;
}

/** Icon shown inside the "reset password" button (avoids a nested ternary in JSX). */
function resetButtonIcon(sending: boolean, sent: boolean) {
    if (sending) return <Spinner />;
    if (sent) return <IconCheck />;
    return <IconMail />;
}

/**
 * `keepFocus` : route `/account/keep?client=<id>` (bouton « Garder Unboared » de
 * l'email de fin d'essai) → l'établissement vient de l'URL (un admin de
 * plusieurs lieux garde le bon) et le bloc « Garder » est mis en avant.
 * `annualFocus` : route `/account/annual?client=<id>` (bouton « Passer à l'annuel »
 * de l'email d'offre annuelle) → même chose pour l'encart annuel ; s'il n'est plus
 * proposable, un message court le dit au lieu de ne rien montrer.
 * `brandFocus` : route `/account/brand?client=<id>` (retour du portail après « Passer en Pro »).
 *
 * Une rubrique par catégorie (cf `utils/accountSections`), choisie dans un menu :
 * colonne à gauche sur ordinateur, liste puis rubrique sur téléphone. `?tab=<id>` dans l'URL.
 */
export function Account({
    keepFocus = false,
    annualFocus = false,
    brandFocus = false,
}: { keepFocus?: boolean; annualFocus?: boolean; brandFocus?: boolean } = {}) {
    const { state } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    // clientID comes from navigation state; fall back to the persisted current
    // clientID so a direct visit / refresh of /account still works (and doesn't
    // query Firestore with an empty id, which throws — cf gotcha). Read localStorage
    // synchronously (once) so the value is correct on the first render and the
    // redirect below doesn't fire spuriously. NB: the key is written by
    // useLocalStorageSelect/usehooks-ts which JSON-encodes it (e.g. `"abc"`), so
    // it must be JSON-parsed — a raw read would keep the quotes and break lookups.
    const [storedClientID] = useState<string>(readStoredClientID);
    // Lien d'un email (« Garder Unboared », « Passer à l'annuel ») : le lieu vient de l'URL.
    const linkFocus = keepFocus || annualFocus || brandFocus;
    // `?client=` présent mais invalide : pas de repli sur le lieu mémorisé (message « lien invalide »).
    const { clientID, invalidLink } = resolveLinkClient({
        linkFocus,
        raw: searchParams.get('client'),
        stateId: state?.clientID,
        storedId: storedClientID,
    });
    const client = useSyncClient(clientID);
    const { auth, sendResetLink, logout } = useAuth();
    const user = useSyncUser(auth?.uid || '');

    // No establishment context at all → nothing to show; send the user home.
    useEffect(() => {
        if (!clientID && !invalidLink) navigate('/home', { replace: true });
    }, [clientID, invalidLink, navigate]);

    /* ---- profile (user doc) ---- */
    const [profile, setProfile] = useState({ firstName: '', lastName: '', email: '' });
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileMsg, setProfileMsg] = useState<Msg>(null);

    /* ---- establishment (client doc) ---- */
    const [company, setCompany] = useState({ name: '', lang: i18n.language });
    const [savingCompany, setSavingCompany] = useState(false);
    const [companyMsg, setCompanyMsg] = useState<Msg>(null);

    /* ---- security ---- */
    const [sendingReset, setSendingReset] = useState(false);
    const [resetSent, setResetSent] = useState(false);

    /* ---- subscription (Stripe, via stripe-b2b) ---- */
    const stripeCustomerId: string | undefined = (client as any)?.stripeCustomerId;
    const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
    // Statut lu (réussi ou non) : distingue « en cours de lecture » de « serveur injoignable ».
    const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
    // Incrémenté après une reprise / annulation de pause → relecture du statut.
    const [subscriptionVersion, setSubscriptionVersion] = useState(0);
    const [openingCancel, setOpeningCancel] = useState(false);
    // Confirmation après « Garder Unboared » (le bloc disparaît à la relecture du statut).
    const [keptNotice, setKeptNotice] = useState<string | null>(null);
    // Aperçu du remerciement hors production (`?keepPreview=1` sur un canal de prévisualisation).
    const keepPreview =
        searchParams.get('keepPreview') === '1' &&
        typeof window !== 'undefined' &&
        window.location.hostname !== 'console.unboared.com';
    // Lien « Garder » : lieu illisible pour ce compte au bout de 8 s → message au lieu d'un chargement sans fin.
    const [clientStuck, setClientStuck] = useState(false);
    useEffect(() => {
        setClientStuck(false);
        if (!linkFocus || client) return undefined;
        const timer = setTimeout(() => setClientStuck(true), 8000);
        return () => clearTimeout(timer);
    }, [linkFocus, client, clientID]);

    // Changement d'établissement : ne jamais afficher l'essai / la pause du précédent.
    useEffect(() => {
        setSubscription(null);
        setSubscriptionLoaded(false);
        setKeptNotice(null);
    }, [stripeCustomerId, clientID]);

    useEffect(() => {
        if (!stripeCustomerId || !clientID) return undefined;
        let cancelled = false;
        // Échec réseau / serveur → null : la page reste complète, seuls les blocs
        // « essai » / « pause » manquent (le bouton portail ne dépend pas de cet appel).
        getSubscriptionStatus(clientID).then((sub) => {
            if (cancelled) return;
            setSubscription(sub);
            setSubscriptionLoaded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [stripeCustomerId, clientID, subscriptionVersion]);

    // « Résilier » : essai → directement le portail Stripe sur l'écran
    // d'annulation (pas de pause pendant l'essai) ; sinon écran « Plutôt faire
    // une pause ? ». Échec de l'ouverture directe → même écran, qui sait se replier.
    const handleCancelClick = async () => {
        logEvent('account_cancel_click', 'Account', { clientID, status: subscription?.status || 'unknown' });
        if (subscription?.status === 'trialing') {
            setOpeningCancel(true);
            try {
                const { url } = await openCancelSession(clientID, i18n.language);
                window.location.assign(url);
                return;
            } catch {
                setOpeningCancel(false);
            }
        }
        navigate('/account/cancel', { state: { clientID } });
    };

    /* ---- session ---- */
    const [logoutErr, setLogoutErr] = useState<string | null>(null);

    useEffect(() => {
        logEvent('screen_view', 'Account');
    }, []);

    useEffect(() => {
        if (user) {
            setProfile((prev) => ({
                ...prev,
                firstName: (user as any)?.firstname || '',
                lastName: (user as any)?.lastname || '',
                email: (user as any)?.email || auth?.email || '',
            }));
        }
    }, [user, auth?.email]);

    useEffect(() => {
        if (client) {
            setCompany({
                name: (client as any)?.name || '',
                lang: (client as any)?.language || i18n.language,
            });
        }
        // i18n.language is only a default fallback; effect is intentionally keyed on client.
    }, [client]);

    const canSaveProfile = useMemo(
        () =>
            !!auth?.uid &&
            (profile.firstName.trim() !== '' || profile.lastName.trim() !== '' || profile.email.trim() !== ''),
        [auth?.uid, profile]
    );

    const handleSaveProfile = async () => {
        if (!auth?.uid) return;
        setSavingProfile(true);
        setProfileMsg(null);
        try {
            const userRef = doc(db, 'users', auth.uid);
            const updates: Record<string, any> = {};
            if (profile.firstName.trim() !== '') updates.firstname = profile.firstName.trim();
            if (profile.lastName.trim() !== '') updates.lastname = profile.lastName.trim();
            if (Object.keys(updates).length > 0) {
                await updateDoc(userRef, updates);
            }

            const currentEmail = (user as any)?.email || auth?.email || '';
            const emailChanged = profile.email.trim() !== '' && profile.email.trim() !== currentEmail;
            if (emailChanged) {
                const authInstance = getAuth();
                if (authInstance.currentUser) {
                    await firebaseUpdateEmail(authInstance.currentUser, profile.email.trim());
                    await updateDoc(userRef, { email: profile.email.trim() });
                }
            }
            setProfileMsg({ type: 'success', text: t('account.updated') });
            setTimeout(() => setProfileMsg(null), 2500);
        } catch (err: any) {
            const code = err?.code || '';
            if (code === 'auth/requires-recent-login' || code === 'CREDENTIAL_TOO_OLD_LOGIN_AGAIN') {
                setProfileMsg({ type: 'error', text: t('account.reauthPrompt') });
            } else if (code === 'auth/email-already-in-use') {
                setProfileMsg({ type: 'error', text: t('account.emailInUse') });
            } else {
                setProfileMsg({ type: 'error', text: t('account.updateFailed') });
            }
        } finally {
            setSavingProfile(false);
        }
    };

    const handleSaveCompany = async () => {
        if (!clientID) return;
        setSavingCompany(true);
        setCompanyMsg(null);
        try {
            const clientRef = doc(db, 'clients', clientID);
            const clientUpdates: Record<string, any> = {};

            const currentCompany = (client as any)?.name || '';
            if (company.name.trim() !== '' && company.name.trim() !== currentCompany) {
                clientUpdates.name = company.name.trim();
            }

            const currentLang = (client as any)?.language || '';
            if (company.lang && company.lang !== currentLang) {
                clientUpdates.lang = company.lang;
                i18n.changeLanguage(company.lang); // `<html lang>` suit via main.tsx
            }

            if (Object.keys(clientUpdates).length > 0) {
                await updateDoc(clientRef, clientUpdates);
            }
            setCompanyMsg({ type: 'success', text: t('account.updated') });
            setTimeout(() => setCompanyMsg(null), 2500);
        } catch (err: any) {
            setCompanyMsg({ type: 'error', text: t('account.updateFailed') });
        } finally {
            setSavingCompany(false);
        }
    };

    const handleResetPassword = async () => {
        const authTmp = getAuth();
        authTmp.languageCode = i18n.language;
        setSendingReset(true);
        setResetSent(false);
        const targetEmail = profile.email || auth?.email || '';
        try {
            await sendResetLink(authTmp, targetEmail);
            setResetSent(true);
            setTimeout(() => setResetSent(false), 2500);
        } catch (err) {
            console.error('Error sending reset password email:', err);
        } finally {
            setSendingReset(false);
        }
    };

    const handleLogout = () => {
        try {
            logEvent('account_logout_click', clientID === 'demo' ? 'Demo' : 'Home');
            logout();
            if (clientID === 'demo') {
                localStorage.removeItem('clientID');
                navigate('/signin');
            }
        } catch (err: any) {
            setLogoutErr(err?.message || t('account.updateFailed'));
        }
    };

    if (!client) {
        // Lien de l'email ouvert avec un compte qui n'a pas accès à ce lieu (lecture refusée :
        // le chargement ne finirait jamais) → explication + portail Stripe en secours.
        if (linkFocus && (clientStuck || invalidLink)) {
            return (
                <LoadingPage>
                    {t('account.keep.noAccess')} <a href={STRIPE_PORTAL_LOGIN_URL}>{t('account.keep.noAccessLink')}</a>
                    <br />
                    <button type="button" className={styles.secondaryBtn} onClick={() => logout()}>
                        {t('account.keep.switchAccount')}
                    </button>
                </LoadingPage>
            );
        }
        return <LoadingPage>{t('home.loadingClient')}</LoadingPage>;
    }

    // « Résilier » : masqué si l'abonnement est déjà fini ou si la résiliation est
    // déjà programmée (le portail « Gérer » permet alors de la retirer). Statut
    // inconnu (serveur injoignable) → affiché : l'écran de résiliation se replie.
    // Réservé aux admins : un membre voit l'abonnement (essai, pause) mais ne le modifie pas.
    const isAdmin = !subscription || subscription.viewerRole !== 'member';
    // Offre annuelle : mensuel au prix catalogue, essai ou actif, admin (cf. annualOfferFor).
    const annualOffer = isAdmin ? annualOfferFor(subscription, i18n.language) : null;
    // « Ma marque » : passage en Pro proposable (mensuel standard au prix catalogue, cf. proOfferFor).
    const proOffer = isAdmin ? proOfferFor(subscription, i18n.language) : null;
    // Plus d'abonnement en cours : reprise directement en Pro (page de paiement Stripe).
    const proEndedOffer =
        isAdmin && !proOffer ? proEndedOfferFor(subscription, i18n.language, getTrialPrice().currency) : null;
    // Rubrique Abonnement : formules au-dessus de l'actuelle, une page Stripe chacune. Arrivée par
    // l'email d'offre annuelle : l'encart annuel habituel (mis en avant), les autres formules à côté.
    // Filtrées par les règles du serveur quand il les donne (`planOptions`) : jamais un bouton voué au refus.
    const planOptions = subscription?.planOptions;
    const upgrades = planUpgradesFor(annualFocus ? null : annualOffer, proOffer, planOptions?.upgrades);
    const brandProOffer = gateProOffer(proOffer || proEndedOffer, planOptions);
    // Ma marque sans bouton Pro : pourquoi (null tant que le statut n'est pas lu). Lieu sans client
    // Stripe (compte offert) : rien à payer en ligne, phrase de contact.
    const proBlockOf = () => {
        if (!stripeCustomerId) return 'other' as const;
        return subscriptionLoaded ? proBlockFor(subscription) : null;
    };
    // Lien de l'email d'offre annuelle : encart mis en avant, ou message si plus proposable.
    const annualLink = annualFocus
        ? annualLinkState({
              hasBilling: !!stripeCustomerId,
              loaded: subscriptionLoaded,
              sub: subscription,
              lang: i18n.language,
          })
        : null;
    // Retour du portail Stripe après « Passer à l'annuel » (stripe-b2b annualSwitch.js : `&from=portal`).
    const annualFromPortal = annualFocus && searchParams.get('from') === 'portal';
    const annualLinkNotice =
        annualFromPortal && annualLink === 'already_yearly'
            ? 'account.annual.switchedDone'
            : (annualLink && ANNUAL_LINK_NOTICES[annualLink]) || null;
    const showCancel =
        isAdmin &&
        (!subscription ||
            (!isCancelScheduled(subscription) &&
                !['canceled', 'incomplete_expired', 'none'].includes(subscription.status)));

    const langOptions = [
        { value: 'en', label: t('global.infos.input.langOptions.en') },
        { value: 'fr', label: t('global.infos.input.langOptions.fr') },
        { value: 'es', label: t('global.infos.input.langOptions.es') },
        { value: 'de', label: t('global.infos.input.langOptions.de') },
    ];

    /* ================= rubriques ================= */
    const hasStripe = !!(client as any)?.stripeCustomerId;
    // Réglages des admins des bars, une fois le rôle connu (en aéroport la langue des
    // joueurs est toujours proposée ; les démos n'ont pas le droit d'écrire ces réglages).
    const venueAdmin =
        isAdmin &&
        (subscriptionLoaded || !stripeCustomerId) &&
        !!clientID &&
        !DEMO_CLIENT_IDS.includes(clientID) &&
        !isAirportClient(clientID);
    const showReferral = hasStripe && !!clientID && canRefer(subscription?.status);

    const panels: Record<AccountSection, React.ReactNode> = {
        subscription: (
            <section className={styles.card} style={{ '--i': 0 } as React.CSSProperties}>
                <div className={styles.cardHead}>
                    <div className={styles.cardIcon}>
                        <IconCard />
                    </div>
                    <div>
                        <div className={styles.cardTitle}>{t('account.subscriptionTitle')}</div>
                    </div>
                </div>
                {annualLinkNotice && (
                    <AnnualLinkNotice
                        clientID={clientID}
                        reason={annualLink as string}
                        messageKey={annualLinkNotice}
                        fromPortal={annualFromPortal}
                    />
                )}
                {(keptNotice || keepPreview) && (
                    <KeepThanksCard detail={keptNotice || t('account.keep.donePaidNoPrice', { date: '29/10' })} />
                )}
                {/* Lien de l'email alors que c'est déjà fait (double clic, autre appareil). */}
                {keepFocus &&
                    !keptNotice &&
                    subscription &&
                    !isCancelScheduled(subscription) &&
                    ['trialing', 'active'].includes(subscription.status) && (
                        <p className={`${styles.msg} ${styles.msgSuccess} ${styles.keepNotice}`} role="status">
                            {t('account.keep.already')}
                        </p>
                    )}
                {keepFocus && !isAdmin && canKeep(subscription) && (
                    <p className={`${styles.msg} ${styles.keepNotice}`} role="status">
                        {t('account.keep.adminOnly')}
                    </p>
                )}
                {isAdmin && canKeep(subscription) && (
                    <KeepSubscriptionCard
                        clientID={clientID}
                        sub={subscription}
                        fromEmail={keepFocus}
                        onKept={(notice) => {
                            setKeptNotice(notice || null);
                            setSubscriptionVersion((v) => v + 1);
                        }}
                    />
                )}
                {subscription && <TrialStatusCard sub={subscription} trialGoal={client?.trialGoal} />}
                {subscription?.pause && (
                    <PauseStatusCard
                        clientID={clientID}
                        sub={subscription}
                        origin="account"
                        onChanged={() => setSubscriptionVersion((v) => v + 1)}
                    />
                )}
                {/* Résiliation déjà programmée : la pause l'annulerait. On la
                    propose aux saisonniers plutôt que de les laisser partir. */}
                {isAdmin &&
                    subscription?.cancelAtPeriodEnd &&
                    subscription.canPause &&
                    subscription.currentPeriodEnd && (
                        <div className={styles.pauseHint}>
                            <p>
                                {t('account.pause.seasonalHint', {
                                    date: unixDate(subscription.currentPeriodEnd, i18n.language),
                                })}
                            </p>
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                onClick={() => navigate('/account/cancel', { state: { clientID } })}
                            >
                                {t('account.pause.seasonalCta')}
                            </button>
                        </div>
                    )}
                {annualFocus && annualOffer && (
                    <AnnualOfferCard
                        clientID={clientID}
                        offer={annualOffer}
                        status={subscription?.status || 'unknown'}
                        fromEmail={annualFocus}
                        fromPortal={annualFromPortal}
                    />
                )}
                {upgrades.length > 0 && (
                    <PlanUpgradesCard
                        clientID={clientID}
                        upgrades={upgrades}
                        from={
                            proOffer?.from === 'annual' || proOffer?.from === 'pro_monthly' ? proOffer.from : 'monthly'
                        }
                        status={subscription?.status || 'unknown'}
                    />
                )}
                <div className={styles.subscriptionActions}>
                    {isAdmin && (
                        <StripeServerForm
                            className={styles.inlineForm}
                            route="/create-customer-portal-session"
                            clientId={clientID}
                            lang={i18n.language}
                        >
                            <button type="submit" className={styles.secondaryBtn}>
                                <IconCard />
                                {t('account.manageSubscription')}
                            </button>
                        </StripeServerForm>
                    )}
                    {showCancel && (
                        <button
                            type="button"
                            className={styles.cancelLink}
                            onClick={handleCancelClick}
                            disabled={openingCancel}
                        >
                            {openingCancel ? t('account.cancel.redirecting') : t('account.cancelSubscription')}
                        </button>
                    )}
                </div>
                {/* Abonnement résilié : le portail propose encore de régler les
                    vieilles factures, ce qui ne réactive rien (même piège que
                    dans HomeUnsubscribedModal). */}
                {isAdmin && subscription?.status === 'canceled' && (
                    <p className={styles.portalHint}>{t('account.pastInvoicesHint')}</p>
                )}
            </section>
        ),
        venue: (
            <>
                <section className={styles.card} style={{ '--i': 0 } as React.CSSProperties}>
                    <div className={styles.cardHead}>
                        <div className={styles.cardIcon}>
                            <IconBuilding />
                        </div>
                        <div>
                            <div className={styles.cardTitle}>{t('account.establishmentTitle')}</div>
                        </div>
                    </div>
                    <div className={styles.grid}>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="companyName">
                                {t('account.companyName')}
                            </label>
                            <input
                                id="companyName"
                                className={styles.input}
                                value={company.name}
                                onChange={(e) => setCompany((c) => ({ ...c, name: e.target.value }))}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="lang">
                                {t('account.lang')}
                            </label>
                            <select
                                id="lang"
                                className={styles.select}
                                value={company.lang}
                                onChange={(e) => setCompany((c) => ({ ...c, lang: e.target.value }))}
                            >
                                {langOptions.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={styles.saveBtn}
                            onClick={handleSaveCompany}
                            disabled={savingCompany}
                        >
                            {savingCompany ? <Spinner /> : <IconSave />}
                            {t('account.save')}
                        </button>
                        <MsgText msg={companyMsg} />
                    </div>
                </section>
                {/* Téléphones des joueurs : choix de la langue. */}
                {venueAdmin && <PlayerLanguageCard clientID={clientID} index={1} />}
            </>
        ),
        // Ma marque (formule Pro) : logo + couleurs sur la TV et les téléphones.
        brand: venueAdmin && (
            <BrandingCard
                key={clientID}
                clientID={clientID}
                venueName={client?.name || ''}
                isPro={client?.plan === 'pro'}
                proOffer={brandProOffer}
                proBlock={proBlockOf()}
                subscriptionEnd={subscription ? keepEndAt(subscription) : null}
                onKeep={() => setSubscriptionVersion((v) => v + 1)}
                onOpenSubscription={() => {
                    logEvent('account_section_open', 'Account', { clientID, section: 'subscription' });
                    const next = new URLSearchParams(searchParams);
                    next.set('tab', 'subscription');
                    setSearchParams(next, { replace: true, state });
                }}
                focus={brandFocus}
                index={0}
            />
        ),
        // Parrainage : clients Stripe en essai ou en règle.
        referral: showReferral && (
            <ReferralCard
                clientID={clientID}
                index={0}
                annualRenewal={
                    subscription?.interval === 'year' && !isCancelScheduled(subscription)
                        ? (subscription.currentPeriodEnd ?? null)
                        : null
                }
            />
        ),
        profile: (
            <>
                <section className={styles.card} style={{ '--i': 0 } as React.CSSProperties}>
                    <div className={styles.cardHead}>
                        <div className={styles.cardIcon}>
                            <IconUser />
                        </div>
                        <div>
                            <div className={styles.cardTitle}>{t('account.profileTitle')}</div>
                        </div>
                    </div>
                    <div className={styles.grid}>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="firstName">
                                {t('account.firstName')}
                            </label>
                            <input
                                id="firstName"
                                className={styles.input}
                                value={profile.firstName}
                                onChange={(e) => setProfile((p) => ({ ...p, firstName: e.target.value }))}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="lastName">
                                {t('account.lastName')}
                            </label>
                            <input
                                id="lastName"
                                className={styles.input}
                                value={profile.lastName}
                                onChange={(e) => setProfile((p) => ({ ...p, lastName: e.target.value }))}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.full}`}>
                            <label className={styles.label} htmlFor="email">
                                {t('account.email')}
                            </label>
                            <input
                                id="email"
                                type="email"
                                className={styles.input}
                                value={profile.email}
                                onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={styles.saveBtn}
                            onClick={handleSaveProfile}
                            disabled={!canSaveProfile || savingProfile}
                        >
                            {savingProfile ? <Spinner /> : <IconSave />}
                            {t('account.save')}
                        </button>
                        <MsgText msg={profileMsg} />
                    </div>
                </section>
                <section className={styles.card} style={{ '--i': 1 } as React.CSSProperties}>
                    <div className={styles.cardHead}>
                        <div className={styles.cardIcon}>
                            <IconShield />
                        </div>
                        <div>
                            <div className={styles.cardTitle}>{t('account.securityTitle')}</div>
                        </div>
                    </div>
                    <p className={styles.hint}>{t('account.securityDesc')}</p>
                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={`${styles.secondaryBtn} ${resetSent ? styles.ok : ''}`}
                            onClick={handleResetPassword}
                            disabled={sendingReset}
                        >
                            {resetButtonIcon(sendingReset, resetSent)}
                            {resetSent ? t('account.emailSuccess') : t('account.resetPassword')}
                        </button>
                    </div>
                </section>
                {/* Cookies : choix du bandeau sur cet appareil (bars seulement). */}
                {clientID && analyticsAllowed({ path: '/account', clientID, mode: client?.mode }) && (
                    <ConsentCard index={2} />
                )}
            </>
        ),
    };

    const summary = subscriptionSummary(subscription, { isPro: client?.plan === 'pro' });
    const subscriptionHint = summary
        ? t(`account.tabs.status.${summary.key}`, {
              count: 'count' in summary ? summary.count : undefined,
              date: 'date' in summary ? unixDate(summary.date, i18n.language) : undefined,
              price: 'amountCents' in summary ? formatAmount(summary.amountCents, summary.currency, i18n.language) : '',
          })
        : t('account.tabs.hint.subscription');
    const menuItems = (
        [
            hasStripe && {
                id: 'subscription',
                label: t('account.tabs.subscription'),
                hint: subscriptionHint,
                icon: <IconCard />,
            },
            { id: 'venue', label: t('account.tabs.venue'), hint: t('account.tabs.hint.venue'), icon: <IconBuilding /> },
            venueAdmin && {
                id: 'brand',
                label: t('account.tabs.brand'),
                hint: t('account.tabs.hint.brand'),
                icon: <IconPalette />,
                badge: 'Pro',
            },
            showReferral && {
                id: 'referral',
                label: t('account.tabs.referral'),
                hint: t('account.tabs.hint.referral'),
                icon: <IconGift />,
            },
            {
                id: 'profile',
                label: t('account.tabs.profile'),
                hint: t('account.tabs.hint.profile'),
                icon: <IconUser />,
            },
        ] as (AccountMenuItem | false)[]
    ).filter((item): item is AccountMenuItem => !!item);

    let focus: AccountFocus = null;
    if (keepFocus) focus = 'keep';
    else if (annualFocus) focus = 'annual';
    else if (brandFocus) focus = 'brand';
    const { section, atMenu } = resolveAccountSection({
        available: menuItems.map((item) => item.id),
        tab: searchParams.get('tab'),
        focus,
        campaign: searchParams.get('utm_campaign'),
        loading: !!stripeCustomerId && !subscriptionLoaded,
    });

    // L'état de navigation (`clientID` d'un admin de plusieurs lieux) suit chaque changement
    // de rubrique : sans lui, la page retomberait sur le dernier lieu mémorisé.
    const openSection = (id: AccountSection) => {
        logEvent('account_section_open', 'Account', { clientID, section: id });
        const next = new URLSearchParams(searchParams);
        next.set('tab', id);
        // Depuis la liste du téléphone : nouvelle entrée d'historique, le « retour » du
        // téléphone revient à la liste. Ensuite, on remplace (pas un pas d'historique par clic).
        setSearchParams(next, { replace: !atMenu, state: { ...state, fromAccountMenu: atMenu } });
        if (atMenu) setTimeout(() => document.getElementById('account-back')?.focus(), 0);
    };
    const backToMenu = () => {
        if (state?.fromAccountMenu) {
            navigate(-1);
        } else {
            // Arrivé par un lien (email, retour de Stripe) : `?tab=menu` l'emporte sur sa rubrique.
            const next = new URLSearchParams(searchParams);
            next.set('tab', ACCOUNT_MENU_TAB);
            setSearchParams(next, { replace: true, state });
        }
        // Après le rendu de la liste (navigate(-1) passe par popstate).
        setTimeout(() => document.getElementById(accountTabId(section))?.focus(), 50);
    };

    const logoutBlock = (
        <>
            <MsgText msg={logoutErr ? { type: 'error', text: logoutErr } : null} />
            <button type="button" className={styles.logoutBtn} onClick={handleLogout}>
                <IconPower />
                {t('home.logout')}
            </button>
        </>
    );

    return (
        <div className={styles.page}>
            <div className={`${styles.shell} ${styles.shellWide}`}>
                {/* ---------- header ---------- */}
                <header className={styles.header}>
                    <div className={styles.brand}>
                        <img src="/assets/images/logo-unboared-sm-tr.png" alt="Unboared" />
                    </div>
                    <div className={styles.headTexts}>
                        <div className={styles.kicker}>{t('home.account')}</div>
                        <h1 className={styles.title}>
                            {t('home.hello')} <span className={styles.name}>{(client as any)?.name}</span> ✌️
                        </h1>
                    </div>
                    <div className={styles.headRight}>
                        {clientID && clientID !== 'demo' && <span className={styles.idPill}>{clientID}</span>}
                        <button type="button" className={styles.closeBtn} onClick={() => navigate('/home')}>
                            <IconClose />
                            {t('global.close')}
                        </button>
                    </div>
                </header>

                {/* Lien de l'email d'offre annuelle vers un lieu sans abonnement Stripe (aéroport, démo…) :
                    au-dessus du menu, visible aussi sur la liste du téléphone. */}
                {annualLinkNotice && !hasStripe && (
                    <section className={styles.card} style={{ '--i': 0 } as React.CSSProperties}>
                        <AnnualLinkNotice
                            clientID={clientID}
                            reason={annualLink as string}
                            messageKey={annualLinkNotice}
                        />
                    </section>
                )}

                <div className={`${styles.layout} ${atMenu ? styles.atMenu : styles.atSection}`}>
                    <nav className={styles.sideNav}>
                        <AccountMenu
                            items={menuItems}
                            active={section}
                            atMenu={atMenu}
                            label={t('home.account')}
                            onSelect={openSection}
                        />
                        <div className={styles.sideLogout}>{logoutBlock}</div>
                    </nav>
                    <div className={styles.sectionPanel}>
                        <button id="account-back" type="button" className={styles.backBtn} onClick={backToMenu}>
                            <IconBack />
                            {t('home.account')}
                        </button>
                        {/* Toutes les rubriques restent montées (une seule visible) : changer de
                            rubrique ne relance ni les mesures « vue » des cartes, ni leur défilement
                            vers l'encart d'un lien, et n'interrompt pas un enregistrement en cours
                            (couleurs de Ma marque, enregistrées après 0,7 s). */}
                        {menuItems.map(({ id }) => (
                            <div
                                key={id}
                                role="tabpanel"
                                id={accountPanelId(id)}
                                aria-labelledby={accountTabId(id)}
                                hidden={id !== section}
                            >
                                {panels[id]}
                            </div>
                        ))}
                        {/* Téléphone : la liste (et sa déconnexion) est cachée dans une rubrique. */}
                        <div className={styles.panelLogout}>{logoutBlock}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
